import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Icon from "shared/components/Icon";

const ADD_TO_BOARD_OPTIONS = [
  {
    id: "insights",
    label: "Insights",
    icon: ({ size = 20, ...props }) => <Icon name="analytics" size={size} color="currentColor" {...props} />,
  },
  {
    id: "funnels",
    label: "Funnels",
    icon: ({ size = 20, ...props }) => <Icon name="funnel" size={size} color="currentColor" {...props} />,
  },
  {
    id: "flows",
    label: "Flows",
    icon: ({ size = 20, ...props }) => <Icon name="flows" size={size} color="currentColor" {...props} />,
  },
  {
    id: "retention",
    label: "Retention",
    icon: ({ size = 20, ...props }) => <Icon name="retention" size={size} color="currentColor" {...props} />,
  },
  {
    id: "text",
    label: "Text",
    icon: ({ size = 20, ...props }) => <Icon name="text" size={size} color="currentColor" {...props} />,
  },
  {
    id: "media",
    label: "Media",
    icon: ({ size = 20, ...props }) => <Icon name="media" size={size} color="currentColor" {...props} />,
  },
];

const QUICK_LINKS_OPTION = {
  id: "quick_links",
  label: "Reports & cards",
  icon: ({ size = 20, ...props }) => <Icon name="analytics" size={size} color="currentColor" {...props} />,
};

/** Home customize only: team overview widgets. */
const TEAM_OVERVIEW_WIDGET_OPTIONS = [
  { id: "team_widget:team_pulse",         label: "Team pulse",         icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:agent_operations",   label: "Agent operations",   icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:web_activity",       label: "Web activity",       icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:product_events",     label: "Product events",     icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:session_engagement", label: "Session engagement", icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:user_momentum",      label: "User momentum",      icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:quality_feedback",   label: "Quality & feedback", icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:reliability_errors", label: "Reliability & errors", icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:tool_performance",   label: "Tool performance",   icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
  { id: "team_widget:workspace_health",   label: "Workspace health",   icon: ({ size = 20, ...p }) => <Icon name="analytics" size={size} color="currentColor" {...p} /> },
];

const DROPDOWN_WIDTH = 280;
const DROPDOWN_MAX_HEIGHT = 420;
const GAP = 6;

export default function AddToBoardDropdown({ open, onClose, anchorRef, onSelectType }) {
  const [isOpen, setIsOpen] = useState(!!open);
  const [position, setPosition] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { team, boardId } = useParams();
  const dropdownRef = useRef(null);
  const showQuickLinks = location.pathname.includes("/home/customize");

  useEffect(() => {
    setIsOpen(!!open);
    if (!open) setPosition(null);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const maxH = Math.min(DROPDOWN_MAX_HEIGHT, vh * 0.8);
    const spaceBelow = vh - anchor.bottom;
    const spaceAbove = anchor.top;
    const openUp = spaceBelow < maxH + GAP && spaceAbove > spaceBelow;
    let top = openUp ? anchor.top - maxH - GAP : anchor.bottom + GAP;
    if (top < 8) top = 8;
    if (top + maxH > vh - 8) top = vh - maxH - 8;
    // Centre horizontally on the anchor pill
    let left = anchor.left + anchor.width / 2 - DROPDOWN_WIDTH / 2;
    if (left + DROPDOWN_WIDTH > vw - 8) left = vw - DROPDOWN_WIDTH - 8;
    if (left < 8) left = 8;
    setPosition({ top, left });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        anchorRef?.current && !anchorRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose, anchorRef]);

  const handleSelect = (id) => {
    onClose?.();
    if (onSelectType) { onSelectType(id); return; }
    const bid = boardId || "new";
    switch (id) {
      case "insights":  navigate(`/${team}/analytics/boards/${bid}/editor?type=insights`); break;
      case "funnels":   navigate(`/${team}/analytics/boards/${bid}/editor?type=funnels`); break;
      case "flows":     navigate(`/${team}/analytics/boards/${bid}/editor?type=flows`); break;
      case "retention": navigate(`/${team}/analytics/boards/${bid}/editor?type=retention`); break;
      case "text":
      case "media":     navigate(`/${team}/analytics/boards/${bid}/editor?type=${id}`); break;
      default: break;
    }
  };

  if (!isOpen || !position) return null;

  const dropdown = (
    <div
      ref={dropdownRef}
      className="rounded-xl border shadow-token-lg overflow-hidden"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: DROPDOWN_WIDTH,
        maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, typeof window !== "undefined" ? window.innerHeight * 0.8 : 420),
        fontFamily: "Inter, sans-serif",
        backgroundColor: "var(--surface-base)",
        borderColor: "var(--border-default)",
        zIndex: 9999,
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div className="px-4 pt-3 pb-2 text-[11px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">
        {showQuickLinks ? "Add to home" : "Add to board"}
      </div>

      {/* Main 3×2 grid for primary options */}
      <div className="grid grid-cols-3 gap-1 px-2 pb-2">
        {ADD_TO_BOARD_OPTIONS.map((item) => {
          const ItemIcon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item.id)}
              className="flex flex-col items-center justify-center gap-2 py-3 px-2 rounded-xl transition-all text-[color:var(--text-secondary)] hover:text-[color:var(--brand-primary)] hover:bg-[color:color-mix(in_srgb,var(--brand-primary)_8%,transparent)]"
            >
              <span
                className="flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
                style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)" }}
              >
                <ItemIcon size={18} />
              </span>
              <span className="text-[11px] font-medium leading-tight text-center text-[color:var(--text-primary)]">
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Home customize extras */}
      {showQuickLinks && (
        <>
          <div className="mx-3 my-1 border-t border-[color:var(--border-default)]" />
          <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">
            Team overview
          </div>
          <div className="pb-1">
            {TEAM_OVERVIEW_WIDGET_OPTIONS.map((item) => {
              const ItemIcon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelect(item.id)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-[color:var(--text-secondary)] hover:bg-[color:color-mix(in_srgb,var(--brand-primary)_8%,transparent)] hover:text-[color:var(--text-primary)]"
                >
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)" }}
                  >
                    <ItemIcon size={14} />
                  </span>
                  <span className="text-[13px] font-medium text-[color:var(--text-primary)]">{item.label}</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => handleSelect(QUICK_LINKS_OPTION.id)}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-[color:var(--text-secondary)] hover:bg-[color:color-mix(in_srgb,var(--brand-primary)_8%,transparent)] hover:text-[color:var(--text-primary)]"
            >
              <span
                className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)" }}
              >
                <Icon name="analytics" size={14} color="currentColor" />
              </span>
              <span className="text-[13px] font-medium text-[color:var(--text-primary)]">{QUICK_LINKS_OPTION.label}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  return createPortal(dropdown, document.body);
}

export { ADD_TO_BOARD_OPTIONS };
